import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Table from "components/DesignSystem/Table/V2/Table";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import { DocumentPreviewModal } from "components/PreviewModal";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useDocPreview } from "hooks/useDocPreview";
import { useQuery } from "hooks/useQuery";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { CreditLedger, useGetCreditsQuery } from "store/apis/refrral";
import { ArrayElement } from "types/utils/ArrayElement";
import { FileIcon } from "components/design/FileIcon";
import { formatDate } from "utils/formatDate";
import { formatNumber } from "utils/formatNumber";
import { TableEmptyState } from "../TableEmptyState/TableEmptyState";
import { CreditsEmptyState } from "components/icons/CreditsEmptyState";

export const CreditsTable = () => {
  const group = useCurrentGroup();
  const query = useQuery();
  const page = Number(query.get("page")) || 1;

  const {
    data: creditHistoryData,
    isLoading,
    isSuccess,
    // refetch,
  } = useGetCreditsQuery(
    { groupId: group.uuid!, pageNum: page },
    { skip: !group.uuid }
  );

  const openPreview = useDocPreview();

  const {
    company_group_credits_ledger = [],
    current_page,
    per_page,
    total_count,
    total_pages,
  } = creditHistoryData || {};

  const createColumn =
    createColumnHelper<
      ArrayElement<CreditLedger["company_group_credits_ledger"]>
    >();

  const columns = [
    createColumn.accessor("created_date", {
      cell: (info) => formatDate(info.getValue()),
      header: "Date",
      size: 25,
    }),

    createColumn.accessor("event_type", {
      size: 35,
      cell: (info) => {
        const tasks = info.row.original.tasks;
        const taskTitles = tasks?.map(({ task_title }) => task_title).join(",");
        const taskId = tasks?.[0]?.task_id;
        const creditEvent = info.getValue();
        const isTaskEvent = creditEvent === "TASK";

        if (isTaskEvent && tasks?.length === 1) {
          return (
            <Link to={`/filings/${taskId}`}>
              <div className="t-text-purple t-text-body-sm">{taskTitles}</div>
            </Link>
          );
        }

        if (isTaskEvent && tasks && tasks?.length > 1) {
          return (
            <ToolTip text={taskTitles as string}>
              <div className="t-text-body-sm t-text-text-60 t-truncate t-w-4/5">
                {taskTitles}
              </div>
            </ToolTip>
          );
        }

        const reason =
          creditEvent.charAt(0) + creditEvent.substring(1).toLowerCase();
        return (
          <div className="t-text-body-sm t-text-text-60">
            {info.row.original.description || reason}
          </div>
        );
      },

      header: "Description",
    }),

    createColumn.accessor("receipt.receipt_document", {
      header: "Receipt",
      size: 25,
      cell: (info) => {
        const receipt = info.getValue();
        return (
          <span>
            {Boolean(receipt) ? (
              <Button
                customType="ghost_icon"
                onClick={() => openPreview(receipt.uuid)}
              >
                <FileIcon fileType={receipt.file_type} size="30" />
              </Button>
            ) : (
              "-"
            )}
          </span>
        );
      },
    }),

    createColumn.accessor("credits", {
      size: 15,
      header: () => <p className="t-m-0 t-text-right t-mr-0">Inkle credits</p>,
      cell: (info) => {
        const operation = info.row.original.operation;

        return (
          <div
            className={classNames(
              "t-text-body-sm t-font-medium t-text-right t-mr-0",
              {
                "t-text-red": operation === "DEBIT",
                "t-text-dark_green": operation !== "DEBIT",
              }
            )}
          >
            {operation === "DEBIT" ? "-" : "+"}
            {formatNumber(info.getValue())}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: company_group_credits_ledger,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (
    isLoading ||
    !isSuccess ||
    current_page === undefined ||
    per_page === undefined ||
    total_count === undefined ||
    total_pages === undefined
  ) {
    return <Loader />;
  }

  const showEmptyState =
    !table.getRowModel().rows.length || table.getRowModel().rows.length < 1;

  return (
    <div>
      <div className="t-flex t-items-center t-justify-end t-text-subtext t-text-text-100 t-py-2">
        <PaginationHandler
          page_num={page}
          per_page={per_page}
          total_count={total_count}
          total_pages={total_pages}
        />
      </div>
      <Table.Container>
        <Table.Content>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: `${header.getSize()}%` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            {!showEmptyState &&
              table.getRowModel().rows.map((row) => (
                <Table.Row key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Content>
      </Table.Container>
      {showEmptyState && (
        <div className="t-mt-[82px]">
          <TableEmptyState
            title="No credit history found"
            icon={<CreditsEmptyState />}
            description="When you have credits, the status starts reflecting here"
          />
        </div>
      )}
      <DocumentPreviewModal />
    </div>
  );
};
